import { useState, FormEvent } from 'react';
import { Navigate, useSearchParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import {
  AuthShell,
  AuthError,
  GoogleSlot,
  authFieldClass,
  authLabelClass,
  authPrimaryButtonClass,
  authGhostButtonClass,
} from '@/components/auth/AuthShell';
import { useValidatedSession } from '@/components/auth/use-validated-session';

/**
 * Sign in with an emailed link.
 *
 * `?redirect=` is set by lib/api.ts when a session expires mid-use, and by the
 * regatta page's claim button. It rides along on the magic token so the trip
 * through the inbox doesn't lose the user's place.
 */
export function LoginPage() {
  const { isAuthenticated, user } = useAuthStore();
  const sessionChecked = useValidatedSession();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [showInviteCode, setShowInviteCode] = useState(false);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const redirectTo = searchParams.get('redirect');
  const claimSlug = searchParams.get('claim');

  // Same guard as signup: a persisted-but-dead token must not redirect you
  // away from the one page that can fix it.
  if (!sessionChecked) return <AuthShell title="Sign in">{null}</AuthShell>;

  if (isAuthenticated) {
    const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
    return <Navigate to={redirectTo || (isAdmin ? '/admin' : '/')} replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await api.post('/auth/magic-link', {
        email: email.trim(),
        tosAccepted: true,
        ...(inviteCode ? { inviteCode } : {}),
        ...(redirectTo ? { redirectTo } : {}),
        ...(claimSlug ? { claimRegattaId: claimSlug } : {}),
      });
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send your sign-in link');
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <AuthShell
        title="Check your email"
        subtitle={
          <>
            We sent a sign-in link to
            <br />
            <strong className="text-viewer-ink">{email.trim()}</strong>
          </>
        }
      >
        <p className="text-center text-[12.5px] text-viewer-faint">
          The link expires in 30 minutes.
        </p>
        <button
          onClick={handleSubmit}
          className={`${authGhostButtonClass} mt-5`}
          disabled={submitting}
        >
          {submitting ? 'Sending…' : 'Resend link'}
        </button>
        <button
          onClick={() => {
            // You clicked this because the address was wrong — don't make them
            // clear it by hand.
            setSent(false);
            setEmail('');
            setInviteCode('');
            setShowInviteCode(false);
            setError(null);
          }}
          className="mt-3 w-full text-center text-[12.5px] text-viewer-accent hover:underline"
        >
          Use different email
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Sign in"
      subtitle={claimSlug ? 'Sign in to claim your boat' : 'Welcome back.'}
    >
      <form onSubmit={handleSubmit} className="space-y-3.5" noValidate>
        <div>
          <label htmlFor="email" className={authLabelClass}>
            Email
          </label>
          <input
            id="email"
            type="email"
            className={authFieldClass}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@yacht.club"
            autoComplete="email"
            required
            autoFocus
          />
          <p className="mt-1 text-[11px] text-viewer-faint">
            We’ll email you a link to sign in. No password to remember.
          </p>
        </div>

        {showInviteCode ? (
          <div>
            <label htmlFor="inviteCode" className={authLabelClass}>
              Invite code
            </label>
            <input
              id="inviteCode"
              className={authFieldClass}
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              autoFocus
            />
          </div>
        ) : (
          <button
            type="button"
            className="text-xs text-viewer-accent hover:underline"
            onClick={() => setShowInviteCode(true)}
          >
            I have an admin code
          </button>
        )}

        <label className="flex items-start gap-2.5 pt-1 cursor-pointer">
          <input
            id="tos"
            type="checkbox"
            checked={tosAccepted}
            onChange={(e) => setTosAccepted(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-viewer-line2/50 bg-viewer-panel accent-brand-dark"
          />
          <span className="text-[11.5px] leading-snug text-viewer-muted">
            I agree to the{' '}
            <Link to="/terms" target="_blank" className="text-viewer-accent hover:underline">
              Terms of service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" target="_blank" className="text-viewer-accent hover:underline">
              Privacy policy
            </Link>
            .
          </span>
        </label>

        <AuthError message={error} />

        <button
          type="submit"
          className={authPrimaryButtonClass}
          disabled={submitting || !email || !tosAccepted}
        >
          {submitting ? 'Sending…' : 'Email me a sign-in link'}
        </button>
      </form>

      <GoogleSlot />

      <p className="mt-4 text-center text-[12.5px] text-viewer-muted">
        New here?{' '}
        <Link
          to={redirectTo ? `/signup?redirect=${encodeURIComponent(redirectTo)}` : '/signup'}
          className="font-semibold text-viewer-accent hover:underline"
        >
          Create an account
        </Link>
      </p>

      <p className="mt-5 border-t border-viewer-line/20 pt-4 text-center text-[11.5px] text-viewer-faint">
        Invited by a regatta organizer? Use the link in your email.
      </p>
    </AuthShell>
  );
}
